import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import api from "@/lib/api";
import { SkeletonRow } from "@/components/Loaders";
import { EmptyState } from "@/components/EmptyState";
import { ChatCircleText, ClockCounterClockwise, CalendarCheck } from "@phosphor-icons/react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CrmControls, priorityRank, PRIORITY_STYLE } from "@/components/CrmControls";

const STATUSES = ["New", "Contacted", "Interested", "Booking Started", "Booking Confirmed", "Completed", "Not Interested", "Closed"];
const STYLE = {
  New: "bg-amber-100 text-amber-700", Contacted: "bg-blue-100 text-blue-700", Interested: "bg-indigo-100 text-indigo-700",
  "Booking Started": "bg-cyan-100 text-cyan-700", "Booking Confirmed": "bg-teal-100 text-teal-700", Completed: "bg-green-100 text-green-700",
  "Not Interested": "bg-stone-200 text-stone-600", Closed: "bg-stone-200 text-stone-600",
};

export default function AdminLeads() {
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState(null);
  const [filter, setFilter] = useState("");
  const [abandoned, setAbandoned] = useState(null);
  const [fu, setFu] = useState(null);
  const [today, setToday] = useState(null);

  const load = () => { setLeads(null); api.get(`/admin/leads${filter ? `?status=${encodeURIComponent(filter)}` : ""}`).then((r) => setLeads([...r.data].sort((a, b) => priorityRank(a.priority) - priorityRank(b.priority)))); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => { if (tab === "abandoned" && abandoned === null) api.get("/admin/abandoned?status=open").then((r) => setAbandoned(r.data)); }, [tab, abandoned]);
  useEffect(() => { if (tab === "followups" && fu === null) { api.get("/admin/followups").then((r) => setFu(r.data)); api.get("/admin/today").then((r) => setToday(r.data)); } }, [tab, fu]);

  const update = async (id, patch) => { await api.patch(`/admin/leads/${id}`, patch); toast.success("Lead updated"); load(); };

  return (
    <DashboardLayout nav={adminNav} title="Admin">
      <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Customer CRM</h1>

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="leads" data-testid="tab-leads">Enquiries</TabsTrigger>
            <TabsTrigger value="followups" data-testid="tab-followups">Follow-ups</TabsTrigger>
            <TabsTrigger value="abandoned" data-testid="tab-abandoned">Abandoned</TabsTrigger>
          </TabsList>
        </Tabs>
        {tab === "leads" && (
          <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="lead-filter" className="w-52 rounded-full"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>

      {tab === "followups" && (
        <div className="mt-4 space-y-5" data-testid="followups-panel">
          {today && (
            <div>
              <p className="mb-2 font-heading font-semibold text-stone-800">Today's Activity</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {[["New owner leads", "new_owner_leads"], ["New customer leads", "new_customer_leads"], ["New properties", "new_properties"], ["Approved", "properties_approved"], ["Booking requests", "booking_requests"], ["Confirmed", "confirmed_bookings"], ["Payments", "payments"], ["Follow-ups due", "followups_due"]].map(([l, k]) => (
                  <div key={k} data-testid={`today-${k}`} className="rounded-xl border border-stone-200 bg-white p-3 text-center"><p className="font-heading text-xl font-bold text-stone-900">{today[k] ?? 0}</p><p className="text-[10px] text-stone-400">{l}</p></div>
                ))}
              </div>
            </div>
          )}
          {fu === null ? <div className="p-4"><SkeletonRow /></div> : ["overdue", "today", "upcoming"].map((b) => (
            <div key={b}>
              <p className="mb-2 flex items-center gap-2 font-heading font-semibold capitalize text-stone-800"><CalendarCheck size={16} className={b === "overdue" ? "text-red-500" : b === "today" ? "text-amber-500" : "text-primary"} />{b} <span className="text-stone-400">({fu.counts[b]})</span></p>
              {fu[b].length === 0 ? <p className="rounded-xl border border-dashed border-stone-200 p-3 text-xs text-stone-400">Nothing here.</p> : (
                <div className="space-y-2">{fu[b].map((l) => (
                  <div key={l.id} data-testid={`fu-${l.id}`} className="flex items-center justify-between rounded-xl border border-stone-200 bg-white px-4 py-2.5">
                    <div><p className="text-sm font-medium text-stone-800">{l.name} <span className={`ml-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${PRIORITY_STYLE[l.priority || "Medium"]}`}>{l.priority || "Medium"}</span></p><p className="text-xs text-stone-400">{l.lead_kind} · {l.phone} · {l.assigned_staff || "unassigned"}</p></div>
                    <span className="text-xs text-stone-500">{(l.next_follow_up || "").slice(0, 10)}</span>
                  </div>
                ))}</div>
              )}
            </div>
          ))}
        </div>
      )}
      {tab === "leads" ? (        <div className="mt-4 overflow-x-auto rounded-2xl border border-stone-200 bg-white">
          {leads === null ? (
            <div className="space-y-2 p-4">{[1, 2, 3].map((i) => <SkeletonRow key={i} />)}</div>
          ) : leads.length === 0 ? (
            <EmptyState icon={ChatCircleText} title="No leads yet" message="Customer enquiries from the Contact page and 'Need help?' forms will appear here." />
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Customer</TableHead><TableHead>Mobile</TableHead><TableHead>Category</TableHead>
                <TableHead>Location</TableHead><TableHead>Source</TableHead><TableHead>Priority / Staff / Follow-up</TableHead><TableHead>Notes</TableHead><TableHead>Status</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {leads.map((l) => (
                  <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                    <TableCell className="font-medium">{l.name}</TableCell>
                    <TableCell>{l.phone}</TableCell>
                    <TableCell>{l.category || "—"}</TableCell>
                    <TableCell>{l.location || "—"}</TableCell>
                    <TableCell className="text-xs text-stone-500">{l.attribution?.source || "direct"}</TableCell>
                    <TableCell><CrmControls lead={l} onUpdate={update} testidPrefix="lead" /></TableCell>
                    <TableCell className="max-w-[180px]"><Input defaultValue={l.notes} onBlur={(e) => e.target.value !== l.notes && update(l.id, { notes: e.target.value })} placeholder="Add note…" className="h-8 rounded-lg text-xs" /></TableCell>
                    <TableCell>
                      <Select value={l.status} onValueChange={(v) => update(l.id, { status: v })}>
                        <SelectTrigger data-testid={`lead-status-${l.id}`} className={`h-8 w-44 rounded-full border-0 text-xs font-semibold ${STYLE[l.status] || "bg-stone-100"}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      ) : tab === "abandoned" ? (
        <div className="mt-4 overflow-x-auto rounded-2xl border border-stone-200 bg-white">
          {abandoned === null ? (
            <div className="space-y-2 p-4">{[1, 2, 3].map((i) => <SkeletonRow key={i} />)}</div>
          ) : abandoned.length === 0 ? (
            <EmptyState icon={ClockCounterClockwise} title="No abandoned bookings" message="Customers who start but don't complete a booking will appear here." />
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>Customer</TableHead><TableHead>Mobile</TableHead><TableHead>Property</TableHead>
                <TableHead>Date</TableHead><TableHead>Last step</TableHead><TableHead>When</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {abandoned.map((a) => (
                  <TableRow key={a.id} data-testid={`abandoned-${a.id}`}>
                    <TableCell className="font-medium">{a.customer_name || "—"}</TableCell>
                    <TableCell>{a.customer_phone || "—"}</TableCell>
                    <TableCell>{a.property_name || "—"}</TableCell>
                    <TableCell>{a.date || "—"}</TableCell>
                    <TableCell className="text-xs text-stone-500">{a.last_step || "started"}</TableCell>
                    <TableCell className="text-xs text-stone-400">{new Date(a.updated_at).toLocaleString("en-IN")}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      ) : null}
    </DashboardLayout>
  );
}
